// biome-ignore format: historic
import { useEffect, useRef, useState } from 'react';

/**
 * Allows you to run a callback when a trigger is invoked. The main difference between
 * this and combining useState and useEffect manually is that you need to take special
 * care when using useState and useEffect to reset the state after every set, otherwise
 * a subsequent set may not change the value and therefore cause a rerender. This hook
 * does that reset for you.
 *
 * Honestly I think this could be done much better. It has three big issues:
 * 1. The reset triggers an additional and unnecessary rerender
 * 2. This hook doesn't enable you to easily have multiple callbacks
 * 3. It calls the callback from inside a useEffect and as such setStates will be batched
 *    which will cause errors if you are setting state and then measuring the DOM changes.
 *
 * Soon I'll reimplement this method using a methodology similar to the following library:
 * https://github.com/lessmess-dev/react-use-trigger/blob/master/index.js
 *
 * @param callback Function to be called upon the trigger being invoked
 */
export default function useTrigger<T>(
  callback: (triggerValue: T) => void,
): React.Dispatch<React.SetStateAction<T | null>> {
  const [trigger, setTrigger] = useState<T | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (trigger === null) return;
    callbackRef.current(trigger);
    setTrigger(null);
  }, [trigger]);

  return setTrigger;
}
